import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaFilm } from "react-icons/fa";
import { MdLiveTv } from "react-icons/md";
import {
  IoHomeOutline,
  IoSettingsOutline,
  IoPeopleOutline,
  IoPersonOutline,
  IoCloudUploadOutline,
  IoFolderOpenOutline,
  IoImagesOutline,
  IoGridOutline,
  IoStatsChartOutline,
  IoMegaphoneOutline,
  IoCardOutline,
  IoCashOutline,
  IoPricetagsOutline,
  IoOptionsOutline,
  IoMenuOutline,
  IoPersonCircleOutline,
  IoShieldCheckmarkOutline,
  IoWarningOutline,
  IoDocumentTextOutline,
  IoHelpCircleOutline,
  IoBookOutline,
} from "react-icons/io5";
import style from "./sidebar.module.css";
import { useRbac } from "../../rbac/RbacContext";
import { useTenant } from "../../context/TenantContext";
import { getSupportTicketCounts } from "../../api/HttpApis";

// Each item optionally declares the permission `module` it needs "view" on.
// Items with no `module` are always visible (e.g. Dashboard). Sections whose
// items all get filtered out are dropped entirely. A full Admin has every
// permission, so they see the complete sidebar unchanged; only limited staff
// see a reduced menu. Real Creators use `creatorSections`, which still runs
// through the same `module` filter below — they pass it because the backend's
// resolveEffectivePermissions (constants/permissions.js) grants full access to
// any non-staff (role !== 0) account, matching requirePermission's own
// exemption for Creators. Don't gate a Creator-only item on a module the
// backend doesn't grant them, or it will silently disappear from the menu.
const adminSections = [
  {
    label: null,
    items: [
      { to: "/mainlayout/dashboard", icon: <IoHomeOutline />, label: "Dashboard", module: "analytics" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/mainlayout/movie", icon: <FaFilm />, label: "Content", module: "content" },
      { to: "/mainlayout/series", icon: <MdLiveTv />, label: "Series", module: "series" },
      { to: "/mainlayout/media-library", icon: <IoImagesOutline />, label: "Library", module: "media" },
    ],
  },
  {
    label: "Manage",
    items: [
      { to: "/mainlayout/submissions", icon: <IoCloudUploadOutline />, label: "Submissions", module: "content" },
      { to: "/mainlayout/bulk-upload", icon: <IoFolderOpenOutline />, label: "Bulk Upload", module: "content", action: "create" },
      { to: "/mainlayout/taxonomyManager", icon: <IoPricetagsOutline />, label: "Taxonomy", module: "categories" },
      { to: "/mainlayout/experience-builder", icon: <IoGridOutline />, label: "Experience Builder", module: "categories" },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/mainlayout/users", icon: <IoPeopleOutline />, label: "Users", module: "users" },
      { to: "/mainlayout/actor-management", icon: <IoPersonOutline />, label: "Actor Management", module: "users" },
    ],
  },
  {
    label: "Access Control",
    items: [
      { to: "/mainlayout/user-management", icon: <IoPersonCircleOutline />, label: "User Management", module: "users" },
      { to: "/mainlayout/roles", icon: <IoShieldCheckmarkOutline />, label: "Roles & Permissions", module: "users" },
    ],
  },
  {
    label: "Monetization",
    items: [
      { to: "/mainlayout/ad-management", icon: <IoMegaphoneOutline />, label: "Ad Management", module: "monetization" },
      { to: "/mainlayout/subscription", icon: <IoCardOutline />, label: "Subscription", module: "monetization" },
      { to: "/mainlayout/monetization/creators", icon: <IoPeopleOutline />, label: "Creator List", module: "monetization", feature: "content_partners_portal" },
      { to: "/mainlayout/monetization/payouts", icon: <IoCashOutline />, label: "Payouts", module: "monetization" },
      { to: "/mainlayout/monetization/watch-fraud-log", icon: <IoWarningOutline />, label: "Suspected Activity", module: "monetization" },
      { to: "/mainlayout/monetization/analytics", icon: <IoStatsChartOutline />, label: "Creator Analytics", module: "monetization", feature: "content_partners_portal" },
      { to: "/mainlayout/monetization/settings", icon: <IoOptionsOutline />, label: "Monetization Settings", module: "monetization" },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/mainlayout/analytics-dashboard", icon: <IoStatsChartOutline />, label: "Analytics", module: "analytics" },
      { to: "/mainlayout/documents", icon: <IoDocumentTextOutline />, label: "Documents", module: "settings" },
      { to: "/mainlayout/support-tickets", icon: <IoHelpCircleOutline />, label: "Support Tickets", module: "support", badge: "supportOpen" },
      { to: "/mainlayout/settings", icon: <IoSettingsOutline />, label: "Settings", module: "settings" },
    ],
  },
];

const creatorSections = [
  {
    label: null,
    items: [
      { to: "/mainlayout/creator-dashboard", icon: <IoHomeOutline />, label: "Dashboard" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/mainlayout/movie", icon: <FaFilm />, label: "Content", module: "content" },
      { to: "/mainlayout/series", icon: <MdLiveTv />, label: "Series", module: "series" },
      { to: "/mainlayout/media-library", icon: <IoImagesOutline />, label: "Library", module: "media" },
    ],
  },
];

// Shown under the creator's Help & Support link. Per-client like
// REACT_APP_LOGO; the email line is hidden when it's unset.
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL;
const SUPPORT_PATH = "/mainlayout/creator-support";
const FAQ_PATH = "/mainlayout/creator-faq";

const Sidebar = () => {
  const location = useLocation();
  const { can } = useRbac();
  // Tenant-specific: pull the per-tenant logo from TenantContext so each tenant
  // renders its own branding instead of a single build-time env logo.
  const { logo, hasFeature } = useTenant();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "1"
  );

  const role = localStorage.getItem("role");
  const rawSections = role === "Admin" ? adminSections : creatorSections;

  const canSeeSupport = role === "Admin" && can("support", "view");

  // Open-ticket count for the Support Tickets badge. Refetched on navigation
  // so it drops right after an admin resolves tickets.
  const [badges, setBadges] = useState({});
  useEffect(() => {
    if (!canSeeSupport) return undefined;
    let alive = true;
    const load = () =>
      getSupportTicketCounts()
        .then((res) => {
          if (alive) setBadges({ supportOpen: res?.data?.open || 0 });
        })
        .catch(() => {});
    load();
    // Fired by the Support Tickets page after a status change.
    window.addEventListener("support-tickets-changed", load);
    return () => {
      alive = false;
      window.removeEventListener("support-tickets-changed", load);
    };
  }, [canSeeSupport, location.pathname]);

  // Permission-gate the menu: keep items with no `module` (always visible) or
  // whose module the user can perform `action` on (default "view" when an item
  // doesn't specify one — e.g. Bulk Upload needs "create" since it's a content-
  // creation feature, not just a viewing one); drop any section left with no
  // items. A full Admin passes every check, so their menu is unchanged. Items
  // can additionally declare a `feature` key required on the tenant's plan
  // (e.g. "content_partners_portal") — those stay hidden regardless of role
  // when the plan doesn't include that feature.
  const sections = rawSections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          (!item.module || can(item.module, item.action || "view")) &&
          (!item.feature || hasFeature(item.feature))
      ),
    }))
    .filter((section) => section.items.length > 0);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", next ? "1" : "0");
      return next;
    });
  };

  const isActive = (to) =>
    location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <div className={`${style.sidebar} ${collapsed ? style.collapsed : ""}`}>
      <div className={style.top}>
        <img
          src={logo}
          alt="Logo"
          className={style.logo}
        />
        <button
          type="button"
          className={style.collapseBtn}
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <IoMenuOutline />
        </button>
      </div>


      <nav className={style.nav}>
        {sections.map((section, i) => (
          <div key={i} className={style.section}>
            {section.label && (
              <div className={style.sectionLabel}>{section.label}</div>
            )}
            {section.items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`${style.navItem} ${
                  isActive(item.to) ? style.navItemActive : ""
                }`}
                title={collapsed ? item.label : undefined}
              >
                <span className={style.navIcon}>{item.icon}</span>
                <span className={style.navLabel}>{item.label}</span>
                {item.badge && badges[item.badge] > 0 && (
                  <span
                    className={style.navBadge}
                    aria-label={`${badges[item.badge]} open`}
                  >
                    {badges[item.badge] > 99 ? "99+" : badges[item.badge]}
                  </span>
                )}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      {role !== "Admin" && (
        <div className={style.footer}>
          <Link
            to={SUPPORT_PATH}
            // Tell the form which page the creator came from.
            state={{ from: isActive(SUPPORT_PATH) ? "" : location.pathname }}
            className={`${style.supportItem} ${
              isActive(SUPPORT_PATH) ? style.navItemActive : ""
            }`}
            title={collapsed ? "Help & Support" : undefined}
          >
            <span className={style.navIcon}>
              <IoHelpCircleOutline />
            </span>
            <span className={style.supportText}>
              <span className={style.navLabel}>Help &amp; Support</span>
              {SUPPORT_EMAIL && (
                <span className={style.supportEmail}>{SUPPORT_EMAIL}</span>
              )}
            </span>
          </Link>
          <Link
            to={FAQ_PATH}
            className={`${style.supportItem} ${isActive(FAQ_PATH) ? style.navItemActive : ""}`}
            title={collapsed ? "FAQ" : undefined}
          >
            <span className={style.navIcon}>
              <IoBookOutline />
            </span>
            <span className={style.supportText}>
              <span className={style.navLabel}>FAQ</span>
            </span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default Sidebar;


